/**
 * The READMODEL widget source — grammar and per-collection metadata (TV1-2291 · §6).
 *
 * This module exists because every widget used to be a Cube query, and the dashboard's
 * operational tiles (`KeyIndicators`, `AlertsSection`) could not be expressed as widgets
 * at all: the grammar had no way to say *"a count from a readmodel"*.
 *
 * ✅ THE MIGRATION IS DONE, and both components are DELETED (TV1-2343). `KeyIndicators`'
 * two counters are the curated KPI row's `count` widgets; three of `AlertsSection`'s
 * four tiles are its `alert` widgets, reading the collections catalogued below. The
 * fourth — "Productos Incompletos" — did NOT come across: its predicate is
 * `readyToPublishChannels`, which TV1-1956 deliberately stopped storing because it is
 * composed from TWO independently-changing streams (the item AND the ML category
 * reference readmodel), so a filterable field for it would be stale by construction.
 * See `curatedReports.ts` (`HEADLINE_KPI_ROW`, the reserved `widgetId( 5, 13 )`).
 *
 * ## What this module is, and is NOT
 *
 * It is **grammar + metadata only**: the filter vocabulary, the per-collection
 * catalog, and a pure `planReadmodelQuery` that turns a stored `ReadmodelQuery`
 * into an executable plan. It performs **no I/O**. The fetch branch lands in
 * `getWidgetData` (ticket 24) and the renderers in ticket 25. Queries go through
 * the generated client library (`backend.queries.*`), never `fetch`.
 *
 * ## Why a catalog exists at all
 *
 * Because the client library's TYPES do not distinguish these collections. Every
 * readmodel query is generated as `ReadmodelQueryBase<Model>` — the same
 * `{ limit, offset, after, sort, filter, select, byDistinctField }` for all of
 * them (`teselly-client-library/src/base/SSE.ts:185`). So the type system will
 * happily let you send a `filter` to a route that ignores it, ask for an `offset`
 * that is silently dropped, or unwrap a response by a key that is not there. Every
 * one of those is a silently-empty widget with no error. The catalog is where
 * those per-readmodel facts live, verified against the server source, one entry at
 * a time.
 *
 * ## ⚠️ The trap this file was commissioned to encode is NOT TRUE. Read this.
 *
 * The SDD (§6) and `.claude/rules/ddd-patterns.md` both assert:
 *
 * > *"A base route with no custom `queryHandler` REJECTS a `filter` query param.
 * > Only the `-search` variant declares `query: defaultQuerySchemas.query` and
 * > applies `params.filter`."*
 *
 * It was verified and it is **false in the current PV3 (`@bett3r-dev/pv3@1.1.7`)**:
 *
 *  1. `registerReadmodelQueryEndpoint` DEFAULTS the query schema —
 *     `query: query.schemas?.query || defaultQuerySchemas.query`
 *     (`pv3/src/libraries/eventsourcing/eventHandlers/index.ts:351`, and identically
 *     at `build/libraries/eventsourcing/eventHandlers/index.js:254`), and
 *     `ReadmodelDefaultQuerySchema` declares `filter`
 *     (`pv3-types/src/eventsourcing/eventHandlers.ts:25`).
 *     `src/services/server/.openapi.json` agrees: `GET /crm/questions/:id?` lists a
 *     `filter` query parameter, `$ref: CollectionFilter`.
 *  2. `defaultQueryHandler` APPLIES it — `repository.query({ limit, after, filter,
 *     sort, select })` (`eventHandlers/index.ts:281`).
 *  3. Production already relies on this: `historyQueryPolicy.ts:32` calls
 *     `backend.queries.BatchProcesses({ filter: [{ field: 'status', op: 'in', … }] })`
 *     against the base `/batch-processes/:id?` route, and its own docblock says
 *     *"Uses server-side filtering, sorting, and pagination via readmodel query
 *     params."*
 *
 * The live precedent the SDD cites — `dataFetchPolicy.ts:127-133`, *"The base
 * /crm/questions route has no custom query handler (so it rejects a server-side
 * `filter`)"* — is therefore a **stale comment**, not a live constraint.
 *
 * ## What IS true about base routes, and why this file still splits the filter
 *
 * Two REAL, verified limitations survive, and they are what `filterMode: 'client'`
 * actually encodes:
 *
 *  - **A base route has no `count`.** It returns a bare array of rows. A `count` /
 *    `progress` widget therefore has to receive the rows and reduce them itself.
 *    That is exactly what `dataFetchPolicy.fetchUnansweredMessages` does, and what
 *    `historyQueryPolicy` does with a second `select: ['id']` round trip.
 *  - **`offset` is SILENTLY DROPPED.** `defaultQueryHandler` destructures
 *    `{ idValue, limit, after, filter, sort, select }` (`eventHandlers/index.ts:272`)
 *    — `offset` is not in the list and is not forwarded (`:281`), even though
 *    `defaultQueryParamsTranslator` computed it two frames earlier (`:86`). A base
 *    route cannot paginate by offset. (`historyQueryPolicy` passes one; it does
 *    nothing.)
 *
 * Given the rows must come back anyway to be counted, the plan for a base-route
 * collection keeps the whole filter client-side: ONE fact decides ONE behaviour,
 * and the truncation trap below cannot be reintroduced by a later edit.
 *
 * ## ⚠️ `limit` must never precede a client-side filter
 *
 * `limit` is applied by the DATABASE, i.e. BEFORE any client-side predicate. So
 * sending `{ limit: 20 }` alongside a client-evaluated filter silently answers a
 * different question: *"how many of the first 20 rows match"*, not *"how many
 * rows match"*. `planReadmodelQuery` therefore moves the limit to `clientLimit`
 * whenever it moves the filter to `clientFilter`; they are never split.
 *
 * ## TRAP 2 — `-search` wrappers are per-readmodel data, never a convention
 *
 * `{ pagePublications, count }` for publications, `{ pageItems, count }` for
 * inventory, `{ pageOrders, count }` for orders, `{ items, count }` for size
 * guides and rule executions, `{ episodes, count }` for drift episodes. There is
 * no rule to infer; each `responseKey` below cites the `.withQuery({ schemas: {
 * response } })` it was read off. Guessing yields an empty widget and no error,
 * which is why `unwrapReadmodelResponse` THROWS on a missing key instead.
 */

// =============================================================================
// Operator vocabulary
// =============================================================================

/**
 * The readmodel filter operators — **16**, taken from
 * `@bett3r-dev/pv3-types/src/database.ts:20-36` (`DatabaseOps`), which is the
 * authoritative list: it is the union the server's `CollectionFilterSchema`
 * enumerates (`database.ts:70`) and the one `filterOpMap` implements
 * (`database.ts:38-58`).
 *
 * ⚠️ **Known drift, deliberately not followed.** The client library declares its
 * own copy at `teselly-client-library/src/base/SSE.ts:147-162` and it is missing
 * `notEmpty` — 15 members, not 16. That is a real drift in a generated-adjacent
 * hand-written file, not a narrowing anyone decided on: `notEmpty` exists on the
 * server (it is the array-valued "present AND non-empty" operator, distinct from
 * `exists`, which is degenerate-true for an always-`[]` field), the wire accepts
 * it, and the FE type merely cannot name it. Since `CollectionFilterInstance.op`
 * on the client is `LooseAutocomplete<DatabaseOps>`, sending `notEmpty` still
 * type-checks — it just loses autocomplete. We list all 16 here; fix the drift in
 * `SSE.ts` when that file is next touched for another reason.
 */
export const READMODEL_FILTER_OPS = [
  'eq',
  'neq',
  'lt',
  'gt',
  'lte',
  'gte',
  'in',
  'nin',
  'contains',
  'notContains',
  'some',
  'none',
  'every',
  'exists',
  'notExists',
  'notEmpty'
] as const;

export type ReadmodelFilterOp = typeof READMODEL_FILTER_OPS[number];

/**
 * Operators that take **no `value` operand**. `filterOpMap` implements them as
 * unary predicates over the row value alone (`pv3-types/src/database.ts:52-57`),
 * and `coerceFilterType` returns `undefined` for `exists` / `notExists`
 * (`pv3/…/eventHandlers/index.ts:39-42`). This is why `ReadmodelFilter.value` is
 * optional rather than `unknown`-and-required.
 */
export const VALUELESS_READMODEL_OPS: ReadonlySet<ReadmodelFilterOp> = new Set<ReadmodelFilterOp>([
  'exists',
  'notExists',
  'notEmpty'
]);

export const isReadmodelFilterOp = ( op: unknown ): op is ReadmodelFilterOp =>
  typeof op === 'string' && ( READMODEL_FILTER_OPS as readonly string[]).includes( op );

// =============================================================================
// Grammar
// =============================================================================

/**
 * One clause of a readmodel filter — the PV3 `CollectionFilterInstance` shape
 * (`pv3-types/src/database.ts:74-78`), narrowed to the operators above.
 *
 * Deliberately NOT the recursive `CollectionFilter` (which also admits `OR` /
 * `AND` branches): the inspector offers a flat conjunction, and PV3 ANDs a flat
 * array anyway. Widening to boolean groups later is additive.
 */
export type ReadmodelFilter = {
  field: string;
  op: ReadmodelFilterOp;
  /** Absent for the valueless operators — see {@link VALUELESS_READMODEL_OPS}. */
  value?: unknown;
};

/**
 * The stored source of a readmodel-backed widget. This is the shape ticket 23
 * mirrors into `uiWidgets-generic.types.ts` as the readmodel source base schema.
 */
export type ReadmodelQuery = {
  collection: string;
  filter?: ReadmodelFilter[];
  limit?: number;
};

/**
 * `true` for a well-formed readmodel filter clause.
 *
 * **This is also the K11 guard.** Cube speaks `{ member, operator, values }`;
 * readmodels speak `{ field, op, value }`. A Cube-shaped `filterOverride` (the
 * dashboard period toolbar — `WidgetLayoutComponent.applyFilterOverrides`) has
 * neither `field` nor `op`, so it fails this predicate and ticket 24 must DROP it
 * rather than translate it. Silent mistranslation is worse than no filtering.
 */
export const isReadmodelFilter = ( value: unknown ): value is ReadmodelFilter => {
  if ( typeof value !== 'object' || value === null ) return false;
  const candidate = value as { field?: unknown; op?: unknown };
  return typeof candidate.field === 'string' && isReadmodelFilterOp( candidate.op );
};

/**
 * TV1-2291 (t25) — `UiWidgetAlertSchema.widgetComponentProps.severity`, the one
 * enum in the readmodel widget grammar.
 *
 * Its three values are `DashboardIndicator`'s `alertLevel` VERBATIM (low = info,
 * medium = warning, high = danger), so a migrated `AlertsSection` tile maps
 * one-to-one. `severity` is REQUIRED in the schema — an alert is the one tile whose
 * colour a seller reads as a judgement about their business, so there is no safe
 * default to fabricate at rest.
 *
 * Lives HERE, in the pure grammar module, rather than in the renderer or the
 * inspector, because THREE surfaces need it and a hand-copied list is what drifts:
 * `AlertWidget` (which class to paint), `ReadmodelSourceSection` (which options to
 * offer) and `WebWidgetValidators.validateAlert` (whether the card is complete).
 * Ticket 26 declared it in the inspector because the renderer did not exist yet;
 * ticket 25 moved it down and the inspector re-exports it unchanged.
 */
export const ALERT_SEVERITIES = [ 'low', 'medium', 'high' ] as const;

export type AlertSeverity = typeof ALERT_SEVERITIES[number];

export const isAlertSeverity = ( value: unknown ): value is AlertSeverity =>
  typeof value === 'string' && ( ALERT_SEVERITIES as readonly string[]).includes( value );

/**
 * The readmodel widget types whose schema declares a `severity`.
 *
 * A SCHEMA fact keyed by type, not a guess from the word "alert": adding a second
 * severity-carrying member means adding its schema first, and then this set.
 */
export const SEVERITY_WIDGET_TYPES: ReadonlySet<string> = new Set(['alert']);

/**
 * Readmodel tiles that RENDER a caption under their number (2026-08-19).
 *
 * The inspector offers the field only for these, because a field that edits a value
 * nothing draws is indistinguishable from a broken one. Derived from the renderers:
 * `count`, `alert` and `progress` each have a `.caption` element; `list` does not
 * (its rows ARE its content and a caption under them has nowhere to sit).
 */
export const CAPTION_WIDGET_TYPES: ReadonlySet<string> = new Set([ 'count', 'alert', 'progress' ]);

/**
 * Readmodel tiles whose NUMBER can be a link.
 *
 * `alert` has had one since t25; `count` joins it on 2026-08-19 (review item 4) —
 * the two are the same tile with and without a severity, so a link on one and not
 * the other was an accident of where the JSX sat, not a decision.
 *
 * ⚠️ `progress` and `list` are deliberately absent. A progress bar's reading is a
 * RATIO, not a thing you can go look at, and a list's rows would each want their own
 * target rather than one for the card. Adding either means designing what the link
 * points AT first.
 *
 * 🔴 Membership here does NOT make an href safe. Every one of these sanitises at the
 * renderer with `sanitizeKpiHref` and fails closed — see the C5 block in
 * `AlertWidget`.
 */
export const LINKABLE_WIDGET_TYPES: ReadonlySet<string> = new Set([ 'alert', 'count' ]);

// =============================================================================
// Catalog
// =============================================================================

/** Where a collection's filter can actually be evaluated. */
export type ReadmodelFilterMode = 'server' | 'client';

export type ReadmodelCollectionMeta = {
  /** Collection / table name — the value stored in `ReadmodelQuery.collection`. */
  collection: string;
  /** Human label for the palette and the inspector. */
  label: string;
  /** One line describing what a row is; shown under the label in the palette. */
  description: string;
  /** Fields the inspector offers as filter targets. Verified filterable/indexed. */
  fields: readonly string[];
  /** Generated client-library operation for the BASE route (returns a bare array). */
  baseOperation: string;
  /** The base route, as registered on the server. Traceability only. */
  baseRoute: string;
  /**
   * Generated client-library operation for the `-search` route, or `null` when the
   * readmodel exposes none.
   */
  searchOperation: string | null;
  /** The `-search` route, as registered on the server, or `null`. */
  searchRoute: string | null;
  /**
   * ⚠️ TRAP 2. The key the `-search` response wraps its rows in — **per-readmodel
   * data, never a convention**. `null` when there is no `-search` route, meaning
   * the response IS the array.
   */
  responseKey: string | null;
  /**
   * A generated operation returning `{ count }` without fetching rows, when the
   * readmodel exposes one. A `count` widget should prefer it — it is the only way
   * to get a total that is not bounded by `limit`.
   */
  countOperation: string | null;
  /**
   * Where this collection's filter is evaluated. `'server'` for every collection
   * with a `-search` route (it returns `{ rows, count }`, so the filter, the limit
   * and the total all resolve in one round trip); `'client'` for base-route-only
   * collections, which return a bare array and therefore have to be reduced here
   * anyway. See the file header for why this is NOT "the base route rejects a
   * filter" — it does not.
   */
  filterMode: ReadmodelFilterMode;
  /** The server file every fact above was read off. */
  verifiedAt: string;
};

/**
 * Per-collection metadata. Every entry was read off the readmodel's own
 * `.withQuery({ … })` declarations and its generated client-library signature —
 * never inferred from the collection's name.
 *
 * The four required by TV1-2291 §5 (the tiles that replace dashboard surfaces)
 * come first; the rest are additive and were verified the same way.
 */
export const READMODEL_CATALOG: Readonly<Record<string, ReadmodelCollectionMeta>> = {
  // ---------------------------------------------------------------------------
  // The four that replace dashboard surfaces
  // ---------------------------------------------------------------------------

  items_publishing_publications_readmodel: {
    collection: 'items_publishing_publications_readmodel',
    label: 'Publicaciones',
    description: 'Una fila por publicación en un canal de venta.',
    // Meilisearch `filterableAttributes` (publications.readmodel.ts:46-67) ∪ the
    // Postgres `ensureIndex` set (:124-146).
    fields: [
      'status',
      'sincronizationStatus',
      'connectorId',
      'integrationUserId',
      'channelIntegrationId',
      'isKit',
      'categoryRef',
      'categoryRefName',
      'tesellyCategoryId',
      'externalCategoryId',
      'driftedFields',
      'hasFreeShipping',
      'delistedAt',
      'lastSyncedAt'
    ],
    baseOperation: 'ItemsPublishingPublications',
    baseRoute: '/items-publishing/publications/:id?',
    searchOperation: 'ItemsPublishingPublicationsSearch',
    searchRoute: '/items-publishing/publications-search/:search?',
    // publications.readmodel.ts:663-666 — `response: S.shape({ pagePublications, count })`.
    responseKey: 'pagePublications',
    // publications.readmodel.ts:797-813 — `/items-publishing/publications-count/`.
    countOperation: 'ItemsPublishingPublicationsCount',
    filterMode: 'server',
    verifiedAt: 'src/services/server/src/modules/items-publishing/publications/publications.readmodel.ts'
  },

  items_publishing_inventory_readmodel: {
    collection: 'items_publishing_inventory_readmodel',
    label: 'Inventario',
    description: 'Una fila por ítem de inventario.',
    fields: [
      'sku',
      'gtin',
      'brand',
      'model',
      'categoryRefName',
      'tesellyCategoryId',
      'tesellyCategoryName',
      'channelIntegrationId',
      'isConflicted',
      'conflictsWith',
      'bomComponentIds',
      'publicationsVariationsIds',
      'relatedIntegrationUserIds',
      'deletedAt'
    ],
    baseOperation: 'ItemsPublishingInventory',
    baseRoute: '/items-publishing/inventory/:id?',
    searchOperation: 'ItemsPublishingInventorySearch',
    searchRoute: '/items-publishing/inventory-search/:search?',
    // inventory.readmodel.ts — `response: S.shape({ pageItems, count })` on the
    // `-search` route. NOTE it is `pageItems`, not `items` and not `pageInventory`.
    responseKey: 'pageItems',
    // No `inventory-count` route exists. `/items-publishing/inventory-stock-break/
    // :threshold?` also returns `{ pageItems, count }` but takes a REQUIRED threshold
    // path param, so it is STILL a bespoke source rather than a member of this
    // grammar. The dashboard's stock-break alert (`alert-stock-breaks`) that used to
    // reach this predicate through a materialized field was retired (TV1-2392 slice 2).
    countOperation: null,
    filterMode: 'server',
    verifiedAt: 'src/services/server/src/modules/items-publishing/items/inventory.readmodel.ts'
  },

  batch_processes_readmodel: {
    collection: 'batch_processes_readmodel',
    label: 'Procesos por lote',
    description: 'Una fila por proceso masivo, con su progreso y su estado.',
    // BatchProcessesReadmodelSchema — teselly-domain/src/batch-processes/
    // batchProcesses.types.ts:493-538.
    fields: [
      'status',
      'operationName',
      'completed',
      'isArchived',
      'totalItems',
      'completedCount',
      'erroredTargetIds',
      'createdAt',
      'updatedAt'
    ],
    baseOperation: 'BatchProcesses',
    baseRoute: '/batch-processes/:id?',
    // batchProcesses.readmodel.ts:123-126 declares ONE query, the base route.
    searchOperation: null,
    searchRoute: null,
    responseKey: null,
    countOperation: null,
    filterMode: 'client',
    verifiedAt: 'src/services/server/src/modules/batch-processes/batchProcesses.readmodel.ts'
  },

  crm_questions_readmodel: {
    collection: 'crm_questions_readmodel',
    label: 'Preguntas',
    description: 'Una fila por pregunta del canal; `type: question` = sin responder.',
    // QuestionSchema (crm.types.ts:9-20) + QuestionsReadmodelSchema
    // (crm-integration.types.ts:15-36).
    fields: [
      'type',
      'answered',
      'connectorId',
      'integrationUserId',
      'channelIntegrationId',
      'publicationId',
      'threadId',
      'receivedAt',
      'answeredAt'
    ],
    baseOperation: 'CrmQuestions',
    baseRoute: '/crm/questions/:id?',
    // questions.readmodel.ts:84-88 declares ONE query, the base route.
    searchOperation: null,
    searchRoute: null,
    responseKey: null,
    countOperation: null,
    filterMode: 'client',
    verifiedAt: 'src/services/server/src/modules/crm/questions.readmodel.ts'
  },

  // ---------------------------------------------------------------------------
  // Additive — verified the same way
  // ---------------------------------------------------------------------------

  sales_orders_readmodel: {
    collection: 'sales_orders_readmodel',
    label: 'Órdenes',
    description: 'Una fila por orden de venta.',
    // Meilisearch `filterableAttributes` (orders.readmodel.ts:133-141) ∩ the
    // Postgres `ensureIndex` set (:144-151).
    fields: [ 'status', 'connectorId', 'integrationId', 'date' ],
    baseOperation: 'SalesOrders',
    baseRoute: '/sales/orders/:id?',
    searchOperation: 'SalesOrdersSearch',
    searchRoute: '/sales/orders-search/:search?',
    // orders.readmodel.ts:362-365 — `response: S.shape({ pageOrders, count })`.
    responseKey: 'pageOrders',
    // orders.readmodel.ts:481-484 — `/sales/orders-count/`.
    countOperation: 'SalesOrdersCount',
    filterMode: 'server',
    verifiedAt: 'src/services/server/src/modules/sales/orders.readmodel.ts'
  },

  items_publishing_publication_drift_episodes_readmodel: {
    collection: 'items_publishing_publication_drift_episodes_readmodel',
    label: 'Episodios de divergencia',
    description: 'Una fila por intervalo en que una publicación difirió del canal.',
    fields: [
      'status',
      'field',
      'publicationId',
      'connectorId',
      'channelIntegrationId',
      'closeReason',
      'openedAt',
      'closedAt'
    ],
    baseOperation: 'ItemsPublishingPublicationDriftEpisodes',
    baseRoute: '/items-publishing/publication-drift-episodes/:id?',
    searchOperation: 'ItemsPublishingPublicationDriftEpisodesSearch',
    searchRoute: '/items-publishing/publication-drift-episodes-search/:search?',
    // publicationDriftEpisodes.readmodel.ts:141-144 — `S.shape({ episodes, count })`.
    responseKey: 'episodes',
    countOperation: null,
    filterMode: 'server',
    verifiedAt: 'src/services/server/src/modules/items-publishing/publications/publicationDriftEpisodes.readmodel.ts'
  },

  automation_rule_executions_readmodel: {
    collection: 'automation_rule_executions_readmodel',
    label: 'Ejecuciones de reglas',
    description: 'Una fila por ejecución de una regla de automatización.',
    // RulesExecutionsReadmodelSchema — teselly-domain/src/automation/
    // rulesExecution-integration.types.ts:68-97.
    fields: [
      'status',
      'ruleId',
      'ruleDefinitionId',
      'ruleDefinitionName',
      'triggeringEventName',
      'triggeringEventId',
      'hierarchy',
      'hierarchyLevel',
      'targetEntityId',
      'batchId',
      'correlationId',
      'startedAt',
      'completedAt',
      'failedAt'
    ],
    baseOperation: 'RuleExecutions',
    baseRoute: '/rule-executions/:id?',
    searchOperation: 'RuleExecutionsSearch',
    searchRoute: '/rule-executions-search/:search?',
    // rulesExecutions.readmodel.ts:147-150 — `S.shape({ items, count })`.
    responseKey: 'items',
    countOperation: null,
    filterMode: 'server',
    verifiedAt: 'src/services/server/src/modules/automation/rulesExecutions.readmodel.ts'
  },

  items_publishing_size_guide_readmodel: {
    collection: 'items_publishing_size_guide_readmodel',
    label: 'Guías de talles',
    description: 'Una fila por guía de talles.',
    fields: [ 'name', 'measureType' ],
    baseOperation: 'ItemsPublishingSizeGuides',
    baseRoute: '/items-publishing/size-guides/:id?',
    searchOperation: 'ItemsPublishingSizeGuidesSearch',
    searchRoute: '/items-publishing/size-guides-search/:search?',
    // sizeGuide.readmodel.ts:130-133 — `S.shape({ items, count })`. Same key as
    // rule executions, a DIFFERENT key from every other entry here: proof the key
    // is data, not a rule.
    responseKey: 'items',
    countOperation: null,
    filterMode: 'server',
    verifiedAt: 'src/services/server/src/modules/items-publishing/size-guides/sizeGuide.readmodel.ts'
  },

  notifications_notifications_readmodel: {
    collection: 'notifications_notifications_readmodel',
    label: 'Notificaciones',
    description: 'Una fila por notificación enviada a un usuario.',
    // NotificationSentEventSchema (notifications-generic.types.ts:11-24) +
    // NotificationsReadmodelSchema (notifications-integration.types.ts:17-20).
    fields: [ 'type', 'read', 'userId', 'title', 'timestamp' ],
    baseOperation: 'Notifications',
    baseRoute: '/notifications/:id?',
    // notifications.readmodel.ts declares `/notifications-count` and the base
    // route — no `-search` variant.
    searchOperation: null,
    searchRoute: null,
    responseKey: null,
    countOperation: 'NotificationsCount',
    filterMode: 'client',
    verifiedAt: 'src/services/server/src/modules/notifications/notifications.readmodel.ts'
  }
};

/** Every collection id the grammar accepts, in catalog order. */
export const READMODEL_COLLECTION_IDS: readonly string[] = Object.keys( READMODEL_CATALOG );

export const isReadmodelCollection = ( collection: string ): boolean =>
  Object.prototype.hasOwnProperty.call( READMODEL_CATALOG, collection );

export const getReadmodelCollection = (
  collection: string
): ReadmodelCollectionMeta | undefined =>
  isReadmodelCollection( collection ) ? READMODEL_CATALOG[collection] : undefined;

/**
 * Thrown when a widget names a collection the catalog does not know.
 *
 * **Loud on purpose.** The alternative — falling back to "no rows" — is
 * indistinguishable from a genuinely empty readmodel, so a typo'd or renamed
 * collection would render a confident `0` forever. `getWidgetData` already has a
 * discriminated error outcome (`WidgetDataState`) and an error tile to put this in.
 */
export class UnknownReadmodelCollectionError extends Error {
  readonly collection: string;

  constructor( collection: string ) {
    super(
      `Unknown readmodel collection "${ collection }". ` +
      `Known collections: ${ READMODEL_COLLECTION_IDS.join( ', ' ) }.`
    );
    this.name = 'UnknownReadmodelCollectionError';
    this.collection = collection;
  }
}

/** {@link getReadmodelCollection}, but throws instead of returning `undefined`. */
export const requireReadmodelCollection = ( collection: string ): ReadmodelCollectionMeta => {
  const meta = getReadmodelCollection( collection );
  if ( !meta ) throw new UnknownReadmodelCollectionError( collection );
  return meta;
};

// =============================================================================
// Planning
// =============================================================================

/**
 * An executable description of ONE readmodel query. Purely derived — building it
 * touches nothing and fetches nothing.
 */
export type ReadmodelQueryPlan = {
  /** The resolved catalog entry. */
  collection: ReadmodelCollectionMeta;
  /** The generated client-library operation to call. */
  operation: string;
  /** The route that operation hits. Traceability / error messages. */
  route: string;
  /** Filter to send to the server. EMPTY on a client-filtered collection. */
  serverFilter: ReadmodelFilter[];
  /**
   * Filter the caller must evaluate over the returned rows — see
   * {@link applyReadmodelFilter}. EMPTY on a server-filtered collection.
   */
  clientFilter: ReadmodelFilter[];
  /** `limit` to send to the server. `undefined` when the plan must see every row. */
  limit: number | undefined;
  /**
   * `limit` to apply AFTER the client-side filter. Never set at the same time as
   * {@link limit} — see the header note on truncation.
   */
  clientLimit: number | undefined;
  /**
   * Key to read the row array out of the response. `null` ⇒ the response IS the
   * array. Feed it to {@link unwrapReadmodelResponse}; do not read it by hand.
   */
  responseKey: string | null;
};

/**
 * Turn a stored `ReadmodelQuery` into a plan.
 *
 * Throws {@link UnknownReadmodelCollectionError} for a collection outside the
 * catalog — see that class for why silence is not an option here.
 */
export const planReadmodelQuery = ( query: ReadmodelQuery ): ReadmodelQueryPlan => {
  const collection = requireReadmodelCollection( query.collection );
  // Drop anything that is not a well-formed clause. In practice this is the K11
  // seam: a Cube-shaped `{ member, operator, values }` override reaching a
  // readmodel widget is IGNORED here rather than mistranslated downstream.
  const filter = ( query.filter ?? []).filter( isReadmodelFilter );
  const filtersOnServer = collection.filterMode === 'server';

  const searchOperation = collection.searchOperation;
  const searchRoute = collection.searchRoute;
  const useSearchRoute = filtersOnServer && searchOperation !== null && searchRoute !== null;

  return {
    collection,
    operation: useSearchRoute ? searchOperation : collection.baseOperation,
    route: useSearchRoute ? searchRoute : collection.baseRoute,
    serverFilter: filtersOnServer ? filter : [],
    clientFilter: filtersOnServer ? [] : filter,
    // The limit travels WITH the filter: applying it server-side while the
    // predicate runs client-side truncates the candidate set before it is
    // filtered, which silently answers a different question.
    limit: filtersOnServer ? query.limit : undefined,
    clientLimit: filtersOnServer ? undefined : query.limit,
    responseKey: useSearchRoute ? collection.responseKey : null
  };
};

// =============================================================================
// Response unwrapping (TRAP 2, made executable)
// =============================================================================

export type ReadmodelRow = Record<string, unknown>;

/**
 * Read the row array out of a readmodel response, by the plan's DECLARED key.
 *
 * A `null` `responseKey` means the response is already the array (every base
 * route: `defaultQueryHandler` returns `repository.query(...)` verbatim —
 * `pv3/…/eventHandlers/index.ts:281`).
 *
 * **Throws when the declared key is absent.** A guessed-or-drifted key otherwise
 * yields `undefined` → `[]` → an empty widget with no error anywhere, which is
 * precisely the failure mode the per-readmodel key table exists to prevent.
 */
export const unwrapReadmodelResponse = (
  plan: ReadmodelQueryPlan,
  response: unknown
): ReadmodelRow[] => {
  if ( plan.responseKey === null ) {
    if ( Array.isArray( response )) return response as ReadmodelRow[];
    if ( response === null || response === undefined ) return [];
    // A base route with an `:id` bound returns the single row, not an array
    // (`defaultQueryHandler` → `data[0] || null`). Normalise to one row.
    return [response as ReadmodelRow];
  }

  const wrapper = response as Record<string, unknown> | null | undefined;
  const rows = wrapper?.[plan.responseKey];

  if ( !Array.isArray( rows )) {
    throw new Error(
      `Readmodel response from ${ plan.route } has no "${ plan.responseKey }" array. ` +
      `The wrapper key is per-readmodel data, not a convention — re-read the ` +
      `response schema in ${ plan.collection.verifiedAt }. ` +
      `Got keys: ${ wrapper ? Object.keys( wrapper ).join( ', ' ) || '(none)' : String( wrapper ) }.`
    );
  }

  return rows as ReadmodelRow[];
};

/**
 * Read the total out of a `-search` response. `-search` routes uniformly carry a
 * sibling `count` alongside their (non-uniform) row key; a base route has none,
 * so the row count is the only total available.
 */
export const readReadmodelCount = (
  plan: ReadmodelQueryPlan,
  response: unknown,
  rows: ReadmodelRow[]
): number => {
  if ( plan.responseKey !== null ) {
    const count = ( response as Record<string, unknown> | null | undefined )?.count;
    if ( typeof count === 'number' ) return count;
  }
  return rows.length;
};

// =============================================================================
// Client-side filter evaluation
// =============================================================================

/** Read a (possibly dotted) field path off a row. */
const readField = ( row: ReadmodelRow, field: string ): unknown =>
  field.split( '.' ).reduce<unknown>(
    ( value, segment ) =>
      typeof value === 'object' && value !== null
        ? ( value as Record<string, unknown> )[segment]
        : undefined,
    row
  );

const asArray = ( value: unknown ): unknown[] => ( Array.isArray( value ) ? value : []);

/**
 * Relational comparison over two same-typed primitives.
 *
 * `filterOpMap` writes these as bare `a < b` on `unknown`s, which TypeScript will
 * not accept without a cast — and a cast here would be a lie (the operands really
 * can be `undefined`). Requiring matching primitive types instead yields exactly
 * `filterOpMap`'s answers: a mixed or missing operand makes JS's `<` false, and
 * makes this `false` too. Strings compare lexicographically, which is what makes
 * `gte` on an ISO date field work.
 */
const relational = (
  actual: unknown,
  expected: unknown,
  compare: ( a: number, b: number ) => boolean,
  compareText: ( a: string, b: string ) => boolean
): boolean => {
  if ( typeof actual === 'number' && typeof expected === 'number' ) return compare( actual, expected );
  if ( typeof actual === 'string' && typeof expected === 'string' ) return compareText( actual, expected );
  return false;
};

/**
 * Evaluate ONE clause against ONE row.
 *
 * Mirrors `filterOpMap` (`pv3-types/src/database.ts:38-58`) operator for
 * operator, so a client-filtered collection answers the same question a
 * server-filtered one would.
 *
 * ⚠️ `filterOpMap` is the **in-memory** implementation. Per
 * `.claude/rules/testing.md`, MemoryDb and Postgres disagree in two places that
 * matter here: `neq` / `nin` over an ABSENT field keep the row in memory
 * (`undefined !== 'x'`) but drop it on Postgres (`NULL <> 'x'` → NULL), and
 * `contains` is array-containment on Postgres but `String.includes` in memory.
 * We follow `filterOpMap` because it is the declared semantics of the operator
 * set — but a widget that filters on an optional field with `neq`/`nin` will not
 * agree with the same filter run server-side. Prefer `exists` + `neq`.
 */
const matchesClause = ( row: ReadmodelRow, clause: ReadmodelFilter ): boolean => {
  const actual = readField( row, clause.field );
  const expected = clause.value;

  switch ( clause.op ) {
    case 'eq': return actual === expected;
    case 'neq': return actual !== expected;
    case 'lt': return relational( actual, expected, ( a, b ) => a < b, ( a, b ) => a < b );
    case 'gt': return relational( actual, expected, ( a, b ) => a > b, ( a, b ) => a > b );
    case 'lte': return relational( actual, expected, ( a, b ) => a <= b, ( a, b ) => a <= b );
    case 'gte': return relational( actual, expected, ( a, b ) => a >= b, ( a, b ) => a >= b );
    case 'in': return asArray( expected ).includes( actual );
    case 'nin': return !asArray( expected ).includes( actual );
    case 'contains': return asArray( actual ).includes( expected );
    case 'notContains': return !asArray( actual ).includes( expected );
    case 'some': return asArray( actual ).some(( entry ) => asArray( expected ).includes( entry ));
    case 'none': return !asArray( actual ).some(( entry ) => asArray( expected ).includes( entry ));
    case 'every': return asArray( actual ).every(( entry ) => asArray( expected ).includes( entry ));
    case 'exists': return !!actual;
    case 'notExists': return !actual;
    case 'notEmpty': return Array.isArray( actual ) && actual.length > 0;
    default: return false;
  }
};

/** Conjunction of every clause — PV3 ANDs a flat filter array. */
export const applyReadmodelFilter = (
  rows: ReadmodelRow[],
  filter: ReadmodelFilter[]
): ReadmodelRow[] =>
  filter.length === 0
    ? rows
    : rows.filter(( row ) => filter.every(( clause ) => matchesClause( row, clause )));

/**
 * Apply a plan's client-side half to the rows the server returned: filter FIRST,
 * then truncate. The order is the whole point — see the header note.
 */
export const applyPlanToRows = (
  plan: ReadmodelQueryPlan,
  rows: ReadmodelRow[]
): ReadmodelRow[] => {
  const filtered = applyReadmodelFilter( rows, plan.clientFilter );
  return plan.clientLimit === undefined ? filtered : filtered.slice( 0, plan.clientLimit );
};
